import React from "react";
import { AlertTriangle } from "lucide-react";

// App-wide error boundary: a render error shows a recoverable fallback instead of
// a blank white screen.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null, resetKey: 0 };
    this.headingRef = React.createRef();
    this.prevTitle = null;
  }

  static getDerivedStateFromError() {
    // Short, shareable id so a user can quote it to support / it can be correlated in logs.
    const errorId = "ERR-" + Math.random().toString(36).slice(2, 8).toUpperCase();
    return { hasError: true, errorId };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`Render error [${this.state.errorId}]:`, error, info?.componentStack);
    // Hook for monitoring (Sentry etc.) if wired later.
    if (typeof window !== "undefined" && window.__onAppError) {
      try { window.__onAppError({ id: this.state.errorId, error, info }); } catch { /* ignore */ }
    }
  }

  componentDidMount() {
    if (this.state.hasError) this.onEnterErrorState();
  }

  componentDidUpdate(_prevProps, prevState) {
    // Move focus + retitle the tab as we enter the error state; restore on recovery.
    if (this.state.hasError && this.state.errorId !== prevState.errorId) {
      this.onEnterErrorState();
    } else if (!this.state.hasError && prevState.hasError) {
      this.restoreTitle();
    }
  }

  componentWillUnmount() {
    this.restoreTitle();
  }

  // On error: retitle the tab and move focus to the heading so the failure is announced
  // to assistive tech (a pre-rendered role="alert" is not reliably spoken).
  onEnterErrorState() {
    if (typeof document !== "undefined") {
      if (this.prevTitle == null) this.prevTitle = document.title;
      document.title = "Something went wrong";
    }
    const focusHeading = () => this.headingRef.current?.focus({ preventScroll: true });
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(focusHeading);
    } else {
      focusHeading();
    }
  }

  restoreTitle() {
    if (typeof document !== "undefined" && this.prevTitle != null) {
      document.title = this.prevTitle;
      this.prevTitle = null;
    }
  }

  // In-tree reset: clear the error and remount children via a key bump — no full reload needed.
  reset = () => this.setState((s) => ({ hasError: false, errorId: null, resetKey: s.resetKey + 1 }));

  render() {
    if (!this.state.hasError) return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
    const action =
      "inline-flex items-center justify-center min-h-11 w-full sm:w-auto px-4 py-3 rounded-xl text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface";
    return (
      <main aria-labelledby="eb-title" className="min-h-screen grid place-items-center bg-muted px-4 py-6 sm:p-6">
        <div className="surface w-full max-w-md p-6 sm:p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-critical-soft grid place-items-center mx-auto mb-4" aria-hidden="true">
            <AlertTriangle className="w-7 h-7 text-critical" aria-hidden="true" focusable="false" />
          </div>
          <div role="alert">
            <h1
              id="eb-title"
              ref={this.headingRef}
              tabIndex={-1}
              aria-describedby="eb-desc"
              className="font-heading font-bold text-xl text-ink focus:outline-none"
            >
              Something went wrong
            </h1>
            <p id="eb-desc" className="text-ink-subtle text-sm mt-2">
              An unexpected error occurred while rendering this page. Try again — if it keeps happening, reload.
            </p>
          </div>
          {this.state.errorId && (
            <p className="text-sm text-ink-subtle mt-2 font-mono">Reference: {this.state.errorId}</p>
          )}
          <div className="mt-6 grid gap-2 sm:flex sm:items-center sm:justify-center">
            <button type="button" onClick={this.reset} className={`${action} bg-primary text-white hover:bg-primary-700`}>
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={`${action} border border-strong text-ink-2 hover:bg-muted`}
            >
              Reload page
            </button>
            <a href="/" className={`${action} border border-strong text-ink-2 hover:bg-muted`}>
              Go home
            </a>
          </div>
        </div>
      </main>
    );
  }
}